import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { SunMedium, Clock, MapPin, CheckCircle, Circle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';


const MEU_DIA_STEPS = [
    { target: '[data-testid="meu-dia"]', title: 'Seu Dia de Trabalho', content: 'Acompanhe sua jornada em tempo real: entradas, saidas, pausas e tempo trabalhado no dia.' },
    { target: '[data-testid="btn-ir-ponto"]', title: 'Registrar Ponto', content: 'Toque aqui para ir direto ao registro de ponto. O sistema valida sua localizacao automaticamente.' },
];

export default function MeuDiaPage() {
    const { user } = useAuth();
    const [registros, setRegistros] = useState([]);
    const [empresa, setEmpresa] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) return;
        const h = { 'Authorization': `Bearer ${token}` };
        Promise.all([
            fetch(`${API}/ponto/registros/today`, { headers: h }).then(r => r.json()),
            fetch(`${API}/ponto/empresa`, { headers: h }).then(r => r.json()),
        ]).then(([reg, emp]) => {
            setRegistros(Array.isArray(reg) ? reg : []); setEmpresa(emp); setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>;

    const now = new Date();
    const lastReg = registros[registros.length - 1];
    const status = !lastReg ? 'Aguardando entrada' : lastReg.tipo === 'entrada' ? 'Trabalhando' : 'Turno encerrado';
    const statusColor = status === 'Trabalhando' ? '#10B981' : status === 'Turno encerrado' ? '#6B7280' : '#F59E0B';

    // Build timeline steps
    const steps = [
        { label: 'Entrada', done: registros.some(r => r.tipo === 'entrada'), time: registros.find(r => r.tipo === 'entrada')?.timestamp_server },
        { label: 'Intervalo', done: registros.filter(r => r.tipo === 'saída').length >= 1 && registros.filter(r => r.tipo === 'entrada').length >= 2, time: null },
        { label: 'Retorno', done: registros.filter(r => r.tipo === 'entrada').length >= 2, time: registros.filter(r => r.tipo === 'entrada')[1]?.timestamp_server },
        { label: 'Saída', done: registros.length >= 4 || (registros.length >= 2 && registros[registros.length-1]?.tipo === 'saída' && registros.filter(r => r.tipo === 'entrada').length < 2), time: registros.length >= 2 ? registros[registros.length-1]?.timestamp_server : null },
    ];

    return (
        <div className="space-y-5" data-testid="meu-dia">
            <ContextualTour pageKey="meu-dia" steps={MEU_DIA_STEPS} />
            {/* Status Banner */}
            <div className="rounded-xl p-5 text-center" style={{ background: `${statusColor}08`, border: `1px solid ${statusColor}25` }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: `${statusColor}15` }}>
                    <SunMedium size={28} style={{ color: statusColor }} />
                </div>
                <p className="text-lg font-bold" style={{ color: statusColor, fontFamily: "var(--font-display)" }}>{status}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                    {now.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                <p className="text-3xl font-bold mt-2" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>
                    {now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </p>
            </div>

            {/* Timeline do Dia */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text)' }}>Jornada do Dia</h3>
                <div className="space-y-0">
                    {steps.map((step, i) => (
                        <div key={step.label} className="flex items-start gap-3">
                            <div className="flex flex-col items-center">
                                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: step.done ? `${statusColor}15` : 'var(--surface-2)', border: `2px solid ${step.done ? statusColor : 'var(--border)'}` }}>
                                    {step.done ? <CheckCircle size={14} style={{ color: statusColor }} /> : <Circle size={14} style={{ color: 'var(--muted)' }} />}
                                </div>
                                {i < steps.length - 1 && <div className="w-0.5 h-8" style={{ background: step.done ? statusColor : 'var(--border)' }} />}
                            </div>
                            <div className="flex-1 pb-3">
                                <p className="text-sm font-medium" style={{ color: step.done ? 'var(--text)' : 'var(--muted)' }}>{step.label}</p>
                                {step.time && (
                                    <p className="text-xs" style={{ color: 'var(--muted)' }}>
                                        {new Date(step.time).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                )}
                                {!step.done && i === steps.findIndex(s => !s.done) && (
                                    <p className="text-[10px] mt-0.5" style={{ color: '#F59E0B' }}>Proxima acao</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Marcações de Hoje */}
            <div className="rounded-xl p-4 sm:p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text)' }}>Marcações Registradas</h3>
                {registros.length === 0 ? (
                    <p className="text-xs text-center py-4" style={{ color: 'var(--muted)' }}>Nenhuma marcação registrada hoje</p>
                ) : (
                    <div className="space-y-1.5">
                        {registros.map((r, i) => (
                            <div key={i} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--surface-2)' }}>
                                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: r.tipo === 'entrada' ? '#10B981' : '#EF4444' }} />
                                <span className="text-sm font-medium flex-1" style={{ color: 'var(--text)' }}>
                                    {r.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                                </span>
                                {r.geo && (
                                    <MapPin size={10} style={{ color: 'var(--muted)' }} title={`${r.geo.lat?.toFixed(4)}, ${r.geo.lon?.toFixed(4)}`} />
                                )}
                                <span className="text-xs" style={{ color: 'var(--muted)' }}>
                                    {new Date(r.timestamp_server || r.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* CTA */}
            <a href="/sistema/ponto" className="block">
                <Button className="btn-gold w-full py-3.5 text-sm font-medium" data-testid="btn-ir-ponto">
                    <Clock size={15} className="mr-2" /> Ir para Ponto Digital
                </Button>
            </a>
        </div>
    );
}
